// ─────────────────────────────────────────────────────────────
// BuddiesHR Landing Page Components
// Copy source: Full Copy Doc (v2), People Platform, self-serve angle.
// ─────────────────────────────────────────────────────────────

/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import HeroVisual from "./visuals/HeroVisual";
import NativeVisual from "./visuals/NativeVisual";
import MeasureVisualArt from "./visuals/MeasureVisual";
import AutomateVisualArt from "./visuals/AutomateVisual";
import PerformVisualArt from "./visuals/PerformVisual";
import EngageVisualArt from "./visuals/EngageVisual";
import {
  APPS,
  BHWordmark,
  ClappyLogo,
  PulsyLogo,
} from "@buddieshr/components/brandLogos";
import { Modal } from "antd";
import { MICROSOFT_TEAMS_LINK } from "@buddieshr/utils/constants";
import TourPlayer from "@buddieshr/components/productTour/tourPlayer";
import GetStartedButton from "@buddieshr/components/getStarted/getStartedButton";

// ═══ HERO ═══
function Hero() {
  // CTA-get-started won the hero A/B/C test ('hero-section-test', ended
  // 2026-10), so it is now the only hero.
  const [tourOpen, setTourOpen] = useState(false);

  return (
    <section className="bh-hero-section" style={{ position: 'relative', padding: '56px 40px 72px', overflow: 'hidden' }}>
      <div className="bh-blob" style={{
        position: 'absolute', top: -140, right: -160, width: 680, height: 680,
        background: 'radial-gradient(circle at 40% 40%, var(--bh-sun-200), var(--bh-sun-100) 55%, transparent 72%)',
        zIndex: 0, filter: 'blur(2px)',
      }} />
      <div className="bh-hero-grid" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, marginLeft: 'max(0px, calc(50vw - 760px))', marginRight: 'auto', display: 'grid', gridTemplateColumns: '1.1fr 1.15fr', gap: 16, alignItems: 'center' }}>
        <div>
          <div className="bh-chip" style={{ marginBottom: 22 }}>
            <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--bh-coral-400)' }} />
            The People Platform · Native in Slack & Teams
          </div>
          <h1 style={{ fontSize: 'clamp(34px, 4.4vw, 58px)', lineHeight: 1.04, letterSpacing: '-0.035em' }}>
            The People Platform <br/>that lives where{' '}<br/>
            <span style={{
              background: 'linear-gradient(95deg, var(--bh-coral-400), var(--bh-sun-400) 60%, var(--bh-plum-400))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontStyle: 'italic',
            }}>your team works.</span>
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.5, color: 'var(--bh-ink-soft)', marginTop: 22, maxWidth: 540 }}>
            Measure engagement, recognize great work, run reviews, and automate the HR busywork. Modular. Native. <strong style={{ color: 'var(--bh-ink)' }}>Try it free in 60 seconds, no sales call required.</strong>
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <GetStartedButton location="hero" id="hero_cta_get_started" className="bh-btn primary">Get started free <span className="bh-arrow">→</span></GetStartedButton>
            <button type="button" id="hero_cta_product_tour" className="bh-btn secondary" onClick={() => setTourOpen(true)}><PlayGlyph size={12} style={{ marginRight: 9 }} />See product tour</button>
          </div>
          <div style={{ marginTop: 22, fontSize: 13, color: 'var(--bh-ink-mute)' }}>
            30-day free trial · Free forever under 10 employees · From $1/user/month
          </div>
          <div style={{ marginTop: 8, fontSize: 13.5 }}>
            <Link href="/demo" style={{
              color: 'var(--bh-ink-soft)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(31,27,22,.25)',
              textUnderlineOffset: 3,
              fontWeight: 500,
            }}>
              Or, book a 15-min demo <span className="bh-arrow">→</span>
            </Link>
          </div>
          <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 16, color: 'var(--bh-ink-mute)', fontSize: 14 }}>
            <AvatarStack />
            <span>⭐ 4.8 on G2 · Trusted by 1,000+ HR teams at fast-moving SMBs</span>
          </div>
          <div className="bh-hero-badges" style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
            <LaurelBadge eyebrow="Featured by" label="Slack" />
            <LaurelBadge eyebrow="Top Rated App" label="Microsoft Teams" />
          </div>
        </div>
        <div className="bh-hero-art" style={{ position: 'relative', width: '100%' }}>
          <div
            className="bh-hero-canvas"
            // Bleeds right of its column, but capped by viewport width so the
            // Slack window's messages always end on screen.
            style={{ width: 'min(165%, max(calc(68.4vw - 47px), calc(115.3vw - 667px)))', maxWidth: 'none', marginLeft: '-16%', marginRight: '-53%' }}
          >
            <HeroVisual
              sizes="(max-width: 520px) 100vw, (max-width: 900px) 480px, (max-width: 1520px) 71vw, 1032px"
              imageStyle={{ filter: 'drop-shadow(0 24px 40px rgba(31,27,22,.14))' }}
            />
          </div>
        </div>
      </div>
      <HeroTourModal open={tourOpen} onClose={() => setTourOpen(false)} />
    </section>
  );
}

function PlayGlyph({ size, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 14" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-1px', ...style }}>
      <path d="M1 1.2v11.6c0 .8.9 1.3 1.6.8l9-5.8c.6-.4.6-1.3 0-1.7l-9-5.8C1.9-.1 1 .4 1 1.2z" fill="currentColor" />
    </svg>
  );
}

// Opened by "See product tour" in the hero.
function HeroTourModal({ open, onClose }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      // Sized by height too, so the chips and CTA under the video stay on
      // screen on a short laptop. 1.53 is the video's ratio; 280px is two
      // rows of chips, the CTA and the gutters.
      width="min(1000px, 94vw, calc((100vh - 280px) * 1.53))"
      centered
      destroyOnHidden
      // Above the fixed site header (z-index 1900).
      zIndex={2000}
      title={null}
      styles={{ content: { padding: 16, borderRadius: 20 } }}
    >
      {/* The /product-tour player, chapter chips included. Destroyed on
          close, which also stops the video. */}
      <TourPlayer autoPlay frameStyle={{ borderRadius: 12 }} />
      <div style={{ marginTop: 20, marginBottom: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <Link href="/get-started" id="hero_tour_cta_get_started" className="bh-btn primary">
          Get started free <span className="bh-arrow">→</span>
        </Link>
        <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)', textAlign: 'center' }}>
          30-day free trial · Free forever under 10 employees · From $1/user/month
        </div>
      </div>
    </Modal>
  );
}

// ═══ HERO PRODUCT SCREEN, Slack window showcasing BuddiesHR apps ═══
function HeroProductScreen() {
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  const msgs = [
    { app: 'clappy', time: '10:42 AM', bot: 'Clappy',
      header: <><b>Maya Kapoor</b> gave kudos to <b>Jordan Lee</b></>,
      card: {
        accent: 'var(--app-clappy)', bg: 'var(--app-clappy-bg)',
        title: '🎉 Shipped the new onboarding flow',
        body: '"Jordan stayed late last week debugging the SSO bug, saved our launch. Living the #craft value."',
        meta: '+120 points · #craft',
        reacts: [['🙌', 12], ['❤️', 8], ['🎯', 3]],
      }},
    { app: 'pulsy', time: '10:44 AM', bot: 'Pulsy',
      header: <>Tuesday pulse, <b>1 min check-in</b></>,
      card: {
        accent: 'var(--app-pulsy)', bg: 'var(--app-pulsy-bg)',
        title: 'How\'s this week going?',
        body: null,
        poll: [['😍 Great', 62], ['🙂 Fine', 28], ['😕 Rough', 10]],
        meta: '42 of 58 responded · anonymous',
      }},
    { app: 'billy', time: '10:47 AM', bot: 'Billy',
      header: <>🎂 It's <b>Priya Shah</b>'s birthday today!</>,
      card: {
        accent: 'var(--app-billy)', bg: 'var(--app-billy-bg)',
        title: 'Wish Priya a happy birthday',
        body: 'Priya has been part of the team for 2 years. Drop a message!',
        reacts: [['🎉', 24], ['🎂', 18], ['💛', 11]],
      }},
  ];
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* floating mascot accents around the window */}
      <FloatingAccent id="palmy" top="-4%"  left="-8%"  size={78}  rot={-12} delay="0s" />
      <FloatingAccent id="alfy"  top="52%"  left="-6%"  size={64}  rot={8}   delay="1.1s" />
      <FloatingAccent id="stany" top="86%"  right="-4%" size={72}  rot={10}  delay="0.6s" />
      <FloatingAccent id="linky" top="-6%"  right="-2%" size={62}  rot={-8}  delay="1.8s" />

      {/* ambient glow */}
      <div style={{
        position: 'absolute', inset: '6% 8%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,176,32,.22), transparent 65%)',
        filter: 'blur(30px)', zIndex: 0,
      }} />

      {/* the Slack-style window */}
      <div style={{
        position: 'relative', zIndex: 2,
        borderRadius: 18,
        background: '#fff',
        boxShadow: '0 30px 60px -20px rgba(31,27,22,.28), 0 10px 24px -8px rgba(31,27,22,.12), 0 0 0 1px rgba(31,27,22,.06)',
        overflow: 'hidden',
        transform: 'perspective(1800px) rotateY(-3deg) rotateX(2deg)',
        transformOrigin: 'center',
      }}>
        {/* window chrome */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#F4EFE7',
          borderBottom: '1px solid rgba(31,27,22,.08)',
        }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
          <div style={{
            marginLeft: 'auto', marginRight: 'auto', fontSize: 11,
            color: 'var(--bh-ink-mute)', fontWeight: 500,
            padding: '3px 14px', background: '#fff', borderRadius: 6,
            border: '1px solid rgba(31,27,22,.06)',
          }}>
            acme-hq.slack.com
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', minHeight: 460 }}>
          {/* Slack sidebar */}
          <aside style={{
            background: '#3F0E40', color: '#fff',
            padding: '14px 0',
            fontSize: 13,
          }}>
            <div style={{ padding: '0 14px 10px', borderBottom: '1px solid rgba(255,255,255,.1)', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Acme Inc</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{ width: 7, height: 7, background: '#4CDE80', borderRadius: '50%' }} />
                Maya Kapoor
              </div>
            </div>
            <div style={{ padding: '0 10px' }}>
              <SlackNav label="Threads" />
              <SlackNav label="Mentions" />
              <SlackNav label="Drafts" />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', fontWeight: 700, padding: '14px 8px 6px', letterSpacing: '.03em' }}>
                BuddiesHR ▾
              </div>
              <SlackNav label="clappy"   bot unread />
              <SlackNav label="pulsy"    bot />
              <SlackNav label="billy"    bot unread />
              <SlackNav label="alfy"     bot />
              <SlackNav label="palmy"    bot />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', fontWeight: 700, padding: '14px 8px 6px', letterSpacing: '.03em' }}>
                Channels
              </div>
              <SlackNav label="general" hash />
              <SlackNav label="random" hash />
              <SlackNav label="eng-wins" hash active />
            </div>
          </aside>

          {/* channel body */}
          <div style={{ display: 'flex', flexDirection: 'column', background: '#fff' }}>
            {/* channel header */}
            <div style={{
              padding: '12px 18px', borderBottom: '1px solid rgba(31,27,22,.08)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--bh-ink)' }}># eng-wins</div>
              <div style={{ fontSize: 12, color: 'var(--bh-ink-mute)' }}>· 58 members</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 11, padding: '4px 9px', background: 'var(--app-clappy-bg)',
                color: 'var(--bh-ink)', borderRadius: 999, fontWeight: 600 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--app-clappy)' }} />
                BuddiesHR active
              </div>
            </div>

            {/* messages */}
            <div style={{ padding: '14px 18px 8px', display: 'flex', flexDirection: 'column', gap: 14, overflow: 'hidden' }}>
              {msgs.map((m, i) => (
                <SlackMessage key={i} msg={m} app={byId[m.app]} />
              ))}
              {/* typing indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--bh-ink-mute)', padding: '2px 2px 10px' }}>
                <div style={{ display: 'flex', gap: 3 }}>
                  <Dot delay="0s" /><Dot delay=".15s" /><Dot delay=".3s" />
                </div>
                <span>Alfy is scheduling donut chats for Friday…</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bh-dot { 0%, 60%, 100% { opacity: .25; } 30% { opacity: 1; } }
      `}</style>
    </div>
  );
}

function Dot({ delay = '0s' }) {
  return (
    <span style={{
      width: 5, height: 5, borderRadius: '50%',
      background: 'var(--bh-ink-mute)',
      animation: `bh-dot 1.1s ease-in-out ${delay} infinite`,
    }} />
  );
}

function SlackNav({ label, bot, hash, active, unread }) {
  return (
    <div style={{
      padding: '4px 8px', borderRadius: 5,
      background: active ? 'rgba(255,255,255,.14)' : 'transparent',
      color: active ? '#fff' : (unread ? '#fff' : 'rgba(255,255,255,.75)'),
      fontWeight: unread || active ? 700 : 500,
      fontSize: 13, display: 'flex', alignItems: 'center', gap: 7,
    }}>
      {bot && <span style={{ fontSize: 9, padding: '1px 4px', background: 'rgba(255,255,255,.15)', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em' }}>APP</span>}
      {hash && <span style={{ opacity: .7 }}>#</span>}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {unread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E85D75' }} />}
    </div>
  );
}

function SlackMessage({ msg, app }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {/* avatar = mascot */}
      <div style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: app.bg }}>
        <app.Logo />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--bh-ink)' }}>{app.name}</span>
          <span style={{ fontSize: 9, padding: '1px 4px', background: 'rgba(31,27,22,.08)', color: 'var(--bh-ink-mute)', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em' }}>APP</span>
          <span style={{ fontSize: 11, color: 'var(--bh-ink-mute)' }}>{msg.time}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--bh-ink-soft)', marginBottom: 6, lineHeight: 1.4 }}>
          {msg.header}
        </div>
        {/* attached card */}
        <div style={{
          borderLeft: `3px solid ${msg.card.accent}`,
          background: msg.card.bg,
          borderRadius: '0 8px 8px 0',
          padding: '10px 12px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bh-ink)', marginBottom: msg.card.body || msg.card.poll ? 4 : 0 }}>
            {msg.card.title}
          </div>
          {msg.card.body && (
            <div style={{ fontSize: 12, color: 'var(--bh-ink-soft)', lineHeight: 1.45, marginBottom: msg.card.meta ? 6 : 0 }}>
              {msg.card.body}
            </div>
          )}
          {msg.card.poll && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '6px 0' }}>
              {msg.card.poll.map(([label, pct]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 11, width: 70, color: 'var(--bh-ink)' }}>{label}</div>
                  <div style={{ flex: 1, height: 7, background: 'rgba(31,27,22,.08)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: msg.card.accent, borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 10, width: 28, textAlign: 'right', color: 'var(--bh-ink-mute)', fontWeight: 600 }}>{pct}%</div>
                </div>
              ))}
            </div>
          )}
          {msg.card.meta && (
            <div style={{ fontSize: 10, color: 'var(--bh-ink-mute)', fontWeight: 600, letterSpacing: '.02em', textTransform: 'uppercase' }}>
              {msg.card.meta}
            </div>
          )}
        </div>
        {msg.card.reacts && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            {msg.card.reacts.map(([e, n]) => (
              <div key={e} style={{
                display: 'flex', alignItems: 'center', gap: 3,
                fontSize: 11, padding: '2px 7px',
                background: '#fff', border: '1px solid rgba(31,27,22,.1)',
                borderRadius: 12, color: 'var(--bh-ink)', fontWeight: 600,
              }}>
                <span>{e}</span><span>{n}</span>
              </div>
            ))}
            <div style={{
              fontSize: 11, padding: '2px 7px',
              background: 'rgba(31,27,22,.05)', borderRadius: 12,
              color: 'var(--bh-ink-mute)',
            }}>+</div>
          </div>
        )}
      </div>
    </div>
  );
}

function FloatingAccent({ id, top, left, right, size, rot = 0, delay = '0s' }) {
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  const app = byId[id];
  if (!app) return null;
  return (
    <div className="bh-float" style={{
      position: 'absolute', top, left, right, width: size, height: size,
      transform: `rotate(${rot}deg)`, zIndex: 3,
      animationDelay: delay,
      filter: 'drop-shadow(0 12px 20px rgba(31,27,22,.22))',
    }}>
      <app.Logo />
    </div>
  );
}

function LaurelBadge({ eyebrow, label }) {
  // Single laurel half, a curving stem with two rows of leaves
  // (outer fan + inner fan) to read as a proper laurel wreath.
  // Mirrored for the right side via scaleX(-1).
  const Laurel = () => (
    <svg width="46" height="62" viewBox="0 0 46 62" fill="none" aria-hidden="true" style={{ display: 'block' }}>
      {/* main curving stem */}
      <path d="M 42 6 C 24 10, 10 24, 4 56" stroke="var(--bh-sun-400)" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      {/* outer leaves, pointing away from center */}
      {[
        { cx: 38, cy: 8,  r: 30,  rx: 5,   ry: 2 },
        { cx: 32, cy: 12, r: 20,  rx: 6,   ry: 2.2 },
        { cx: 25, cy: 18, r: 8,   rx: 6.5, ry: 2.3 },
        { cx: 19, cy: 26, r: -4,  rx: 7,   ry: 2.4 },
        { cx: 13, cy: 35, r: -18, rx: 7,   ry: 2.4 },
        { cx: 9,  cy: 44, r: -32, rx: 6.5, ry: 2.3 },
        { cx: 6,  cy: 53, r: -45, rx: 5.5, ry: 2.1 },
      ].map((l, i) => (
        <ellipse key={`o${i}`} cx="0" cy="0" rx={l.rx} ry={l.ry} fill="var(--bh-sun-400)"
          transform={`translate(${l.cx} ${l.cy}) rotate(${l.r})`} />
      ))}
      {/* inner leaves, smaller, pointing toward the text */}
      {[
        { cx: 35, cy: 14, r: -130, rx: 4.5, ry: 1.7 },
        { cx: 28, cy: 22, r: -140, rx: 5,   ry: 1.8 },
        { cx: 22, cy: 31, r: -150, rx: 5,   ry: 1.8 },
        { cx: 17, cy: 40, r: -160, rx: 4.8, ry: 1.7 },
        { cx: 13, cy: 50, r: -170, rx: 4.5, ry: 1.6 },
      ].map((l, i) => (
        <ellipse key={`i${i}`} cx="0" cy="0" rx={l.rx} ry={l.ry} fill="var(--bh-sun-400)" opacity="0.85"
          transform={`translate(${l.cx} ${l.cy}) rotate(${l.r})`} />
      ))}
      {/* tip cap */}
      <ellipse cx="42" cy="6" rx="3" ry="1.3" fill="var(--bh-sun-400)" transform="rotate(-32 42 6)" />
    </svg>
  );
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
      <Laurel />
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
        <span style={{
          fontSize: 10.5, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.14em',
          color: 'var(--bh-ink-mute)',
          whiteSpace: 'nowrap',
        }}>{eyebrow}</span>
        <span style={{
          fontFamily: 'var(--bh-font-display)',
          fontSize: 17, fontWeight: 600,
          letterSpacing: '-0.01em', color: 'var(--bh-ink)',
          whiteSpace: 'nowrap',
        }}>{label}</span>
      </div>
      <div style={{ transform: 'scaleX(-1)' }}><Laurel /></div>
    </div>
  );
}

function AvatarStack() {
  const people = [
    { src: '/img/love/kat.jpg', name: 'Kat' },
    { src: '/img/love/kevin.jpg', name: 'Kevin' },
    { src: '/img/love/carlye.jpeg', name: 'Carlye' },
    { src: '/img/love/arti.jpg', name: 'Arti' },
    { src: '/img/love/victor.jpeg', name: 'Victor' },
  ];
  return (
    <div style={{ display: 'flex' }}>
      {people.map((p, i) => (
        <img
          key={p.src}
          src={p.src}
          alt={`${p.name}, BuddiesHR customer`}
          style={{
            width: 30, height: 30, borderRadius: '50%',
            objectFit: 'cover', background: 'var(--bh-line)',
            border: '2px solid var(--bh-cream)',
            marginLeft: i === 0 ? 0 : -10, zIndex: 10 - i,
          }}
        />
      ))}
    </div>
  );
}

function HeroMascotCluster() {
  const positions = [
    ['clappy',  48,  6, 140,  -6, 'bh-float'],
    ['billy',   10, 24, 120,  4,  'bh-float2'],
    ['alfy',    78, 28, 130, -4,  'bh-float2'],
    ['pulsy',   30, 48, 135,  6,  'bh-float'],
    ['palmy',   64, 54, 130, -5,  'bh-float2'],
    ['linky',    2, 58, 110,  8,  'bh-float'],
    ['stany',   70, 82, 130, -3,  'bh-float'],
    ['perf',    20, 78, 120,  5,  'bh-float2'],
  ];
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  return (
    <div style={{ position: 'relative', height: 560, width: '100%' }}>
      <div style={{
        position: 'absolute', inset: '20% 15%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,176,32,.18), transparent 65%)',
        filter: 'blur(20px)',
      }} />
      {positions.map(([id, l, t, size, rot, anim]) => {
        const app = byId[id];
        return (
          <div key={id} className={anim} style={{
            position: 'absolute', left: `${l}%`, top: `${t}%`,
            width: size, height: size, transform: `rotate(${rot}deg)`,
            animationDelay: `${(l + t) * 0.02}s`,
          }}>
            <div style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 14px 24px rgba(31,27,22,.18))' }}>
              <app.Logo />
            </div>
          </div>
        );
      })}
      <Sparkle left="45%" top="20%" size={20} delay="0s" />
      <Sparkle left="20%" top="42%" size={14} delay="1.2s" />
      <Sparkle left="85%" top="48%" size={18} delay="0.6s" />
      <Sparkle left="55%" top="76%" size={16} delay="2s" />
    </div>
  );
}

function Sparkle({ left, top, size = 16, delay = '0s', color = 'var(--bh-sun-400)' }) {
  return (
    <svg style={{
      position: 'absolute', left, top, width: size, height: size,
      animation: `bh-pop-in 2.5s ease-in-out ${delay} infinite alternate`,
    }} viewBox="0 0 20 20" fill={color}>
      <path d="M10 0 l2.5 7.5 L20 10 l-7.5 2.5 L10 20 l-2.5 -7.5 L0 10 l7.5 -2.5 z" />
    </svg>
  );
}

// ═══ LOGO MARQUEE ═══
function LogoMarquee() {
  const logos = [
    { src: '/img/clients/docplanner.svg', alt: 'Docplanner' },
    { src: '/img/clients/ouraring.com.png', alt: 'Oura' },
    { src: '/img/clients/mercari.svg', alt: 'Mercari' },
    { src: '/img/clients/spotnana.png', alt: 'Spotnana' },
    { src: '/img/clients/safetywing.png', alt: 'SafetyWing' },
    { src: '/img/clients/getcensus.com.svg', alt: 'Census' },
    { src: '/img/clients/goodr.com.png', alt: 'Goodr' },
    { src: '/img/clients/gatik.ai.png', alt: 'Gatik' },
    { src: '/img/clients/joor.com.svg', alt: 'JOOR' },
    { src: '/img/clients/unglobalcompact.org.svg', alt: 'UN Global Compact' },
    { src: '/img/clients/maymobility.png', alt: 'May Mobility' },
    { src: '/img/clients/routeware.com.png', alt: 'Routeware' },
    { src: '/img/clients/qure.ai.svg', alt: 'Qure.ai' },
    { src: '/img/clients/chatdesk.png', alt: 'Chatdesk', h: 22 },
    { src: '/img/clients/phantombuster.png', alt: 'PhantomBuster' },
  ];
  return (
    <section style={{ padding: '40px 0 8px', borderTop: '1px solid var(--bh-line)', borderBottom: '1px solid var(--bh-line)', background: 'var(--bh-cream-deep)', overflow: 'hidden' }}>
      <p style={{ textAlign: 'center', color: 'var(--bh-ink-mute)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginBottom: 24 }}>
        1,000+ HR teams run on BuddiesHR
      </p>
      <div style={{ display: 'flex', gap: 56, alignItems: 'center', animation: 'bh-marquee 40s linear infinite', whiteSpace: 'nowrap', paddingBottom: 28, width: 'max-content' }}>
        {[...logos, ...logos, ...logos].map((l, i) => (
          <img
            key={i}
            src={l.src}
            alt={`${l.alt} uses BuddiesHR`}
            style={{ height: l.h || 30, width: 'auto', display: 'block', flexShrink: 0, opacity: 0.55, filter: 'brightness(0)' }}
          />
        ))}
      </div>
      <style>{`@keyframes bh-marquee { to { transform: translateX(-33.33%); } }`}</style>
    </section>
  );
}

// ═══ "WHY TEAMS LEAVE", 3 problems (Tabbed swap) ═══
function ProblemEvidence({ active }) {
  if (active === 0) {
    // 100-dot grid: 78 didn't respond, 22 did. Each dot is a person.
    const CELLS = Array.from({ length: 100 }, (_, i) => i < 78);
    return (
      <div style={{
        background: 'var(--bh-cream-deep)',
        border: '1.5px solid var(--bh-line)',
        borderRadius: 'var(--bh-r-md)',
        padding: '22px 24px',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--bh-ink-mute)', letterSpacing: '.04em', marginBottom: 14, textTransform: 'uppercase' }}>
          In a 100-person team, on a portal-based tool…
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'center' }}>
          {/* 10×10 dot grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: 5,
            padding: 4,
          }}>
            {CELLS.map((didntRespond, i) => (
              <span key={i} style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: '50%',
                background: didntRespond ? 'rgba(31,27,22,.14)' : '#E85D75',
                opacity: didntRespond ? 1 : 1,
              }} />
            ))}
          </div>
          {/* Side callout */}
          <div>
            <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 56, fontWeight: 700, color: '#E85D75', letterSpacing: '-0.04em', lineHeight: 0.95 }}>
              78%
            </div>
            <div style={{ fontSize: 14, color: 'var(--bh-ink)', lineHeight: 1.35, fontWeight: 600, marginTop: 6 }}>
              people quietly ignore your survey
            </div>
            <div style={{
              marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--bh-line)',
              display: 'grid', gap: 6, fontSize: 12.5, color: 'var(--bh-ink-soft)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#E85D75', flexShrink: 0 }} />
                <span><strong style={{ color: 'var(--bh-ink)' }}>22</strong> respond</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'rgba(31,27,22,.14)', flexShrink: 0 }} />
                <span><strong style={{ color: 'var(--bh-ink)' }}>78</strong> don't</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (active === 1) {
    // Pricing comparison rows
    const rows = [
      { name: 'Culture Amp', price: '$9', sub: '/user/mo', tag: 'Annual contract · sales-gated', bad: true },
      { name: 'Lattice', price: '$11', sub: '/user/mo', tag: '"Talk to sales" for real pricing', bad: true },
      { name: '15Five', price: '$10', sub: '/user/mo', tag: 'Per-product upsell stack', bad: true },
    ];
    return (
      <div style={{
        background: 'var(--bh-cream-deep)',
        border: '1.5px solid var(--bh-line)',
        borderRadius: 'var(--bh-r-md)',
        padding: '18px 20px',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--bh-ink)', marginBottom: 12, letterSpacing: '.04em' }}>What a 100-person team actually pays</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map(r => (
            <div key={r.name} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', background: '#fff',
              border: '1px solid var(--bh-line)',
              borderRadius: 10,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--bh-ink)' }}>{r.name}</div>
                <div style={{ fontSize: 11, color: 'var(--bh-ink-mute)', marginTop: 1 }}>{r.tag}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontFamily: 'var(--bh-font-display)', fontSize: 20, fontWeight: 700, color: '#E85D75', letterSpacing: '-0.02em' }}>{r.price}</span>
                <span style={{ fontSize: 11, color: 'var(--bh-ink-mute)', marginLeft: 2 }}>{r.sub}</span>
                <div style={{ fontSize: 10, color: '#E85D75', marginTop: 2, fontWeight: 600 }}>≈ ${Math.round(r.price.replace('$','') * 100 * 12 / 1000)}k/yr</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--bh-ink-mute)', lineHeight: 1.5 }}>
          Public list prices, billed annually. Most teams sign before they've used the product.
        </div>
      </div>
    );
  }
  // active === 2, onboarding timeline
  const stages = [
    { w: 'W1', t: 'Kickoff call', who: 'Their CSM' },
    { w: 'W2', t: 'Data import + SSO', who: 'You + IT' },
    { w: 'W3', t: 'Config workshop', who: 'You + 2 admins' },
    { w: 'W4', t: 'Pilot rollout', who: '1 team' },
    { w: 'W5+', t: 'Company launch', who: 'If you survive' },
  ];
  return (
    <div style={{
      background: 'var(--bh-cream-deep)',
      border: '1.5px solid var(--bh-line)',
      borderRadius: 'var(--bh-r-md)',
      padding: '18px 20px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--bh-ink)', marginBottom: 14, letterSpacing: '.04em' }}>Typical "enterprise platform" rollout</div>
      <div style={{ position: 'relative', paddingLeft: 6 }}>
        <div style={{ position: 'absolute', left: 13, top: 8, bottom: 8, width: 2, background: 'rgba(232,93,117,.25)' }} />
        <div style={{ display: 'grid', gap: 10 }}>
          {stages.map((s, i) => (
            <div key={s.w} style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#fff', border: '2px solid #E85D75',
                color: '#E85D75', fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, position: 'relative', zIndex: 1,
              }}>{i + 1}</div>
              <div style={{ flex: 1, padding: '8px 12px', background: '#fff', border: '1px solid var(--bh-line)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#E85D75', letterSpacing: '.06em' }}>{s.w}</span>
                  <span style={{ fontSize: 13, color: 'var(--bh-ink)', fontWeight: 600, marginLeft: 8 }}>{s.t}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--bh-ink-mute)' }}>{s.who}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--bh-ink-mute)', lineHeight: 1.5 }}>
        Weeks of work before a single employee sees value.
      </div>
    </div>
  );
}

// ═══ "WHY TEAMS LEAVE", 3 problems (Tabbed swap) ═══
function WhyLeave() {
  const [active, setActive] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);
  const sectionRef = React.useRef(null);
  // Refs to each tab's bottom-border bar, so we can fill them on scroll
  // without re-rendering this whole (large) section on every frame.
  const barRefs = React.useRef([]);
  React.useEffect(() => {
    // Flatten on narrow widths AND on short viewports (<980px tall),
    // where the pinned 100vh scroll-jack content would overflow/clip.
    const mq = window.matchMedia('(max-width: 900px), (max-height: 979px)');
    setIsMobile(mq.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // Desktop: advance the active tab as the pinned section scrolls past.
  React.useEffect(() => {
    if (isMobile) return; // mobile uses the flat stack, no scroll-jacking
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) return;
      const progress = Math.max(0, Math.min(1, -rect.top / total));
      const idx = progress < 1 / 3 ? 0 : progress < 2 / 3 ? 1 : 2;
      setActive((cur) => (cur === idx ? cur : Math.max(0, Math.min(2, idx))));
      // Fill each tab's bottom border to show scroll position: tabs already
      // passed read full, the current tab fills across its third, the rest empty.
      for (let i = 0; i < 3; i++) {
        const bar = barRefs.current[i];
        if (!bar) continue;
        const fill = Math.max(0, Math.min(1, (progress - i / 3) / (1 / 3)));
        bar.style.width = `${fill * 100}%`;
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isMobile]);
  const problems = [
    {
      n: '01',
      chip: 'Portal fatigue',
      bad: 'Nobody opens them.',
      badLong: 'Surveys live on a portal. Recognition lives on a portal. Reviews live on a portal. Employees forget the URL, lose the password, and your engagement program turns into a 12% response rate.',
      fix: 'BuddiesHR runs natively inside Slack and Microsoft Teams.',
      bullets: [
        ['Pulsy', 'surveys arrive as a Slack/Teams message. People answer in one tap.', 'var(--app-pulsy)'],
        ['Clappy', 'kudos happen in the channel where work already happens.', 'var(--app-clappy)'],
        ['Billy', 'posts birthdays in #general. The whole team sees them.', 'var(--app-billy)'],
        ['Palmy', 'lets people request PTO with a slash command.', 'var(--app-palmy)'],
      ],
      kicker: 'Same data. Same dashboards for HR. ',
      kickerBold: '3–5× the participation.',
      stat: { big: '80%+', small: 'avg response rate vs. ~20–30% on portal-based tools', barPct: 80, barLabel: 'Avg response rate in Slack / Teams' },
      accent: 'var(--app-clappy)',
      accentBg: 'var(--app-clappy-bg)',
      badIcon: '🚪',
    },
    {
      n: '02',
      chip: 'Pricing trap',
      bad: "Built for budgets you don't have.",
      badLong: 'Culture Amp starts at ~$9/user/month. Lattice at ~$11. Both gate pricing behind sales calls and annual contracts. Both assume you have a 5-person People Ops team.',
      fix: '$1/user/month for one app. $2 for the full Culture OS suite. $3 with SimplePerf.',
      bullets: [
        ['$1', '/user/mo for any single app', 'var(--app-clappy)'],
        ['$2', '/user/mo for full Culture OS', 'var(--app-pulsy)'],
        ['$3', '/user/mo with SimplePerf included', 'var(--app-perf)'],
        ['Free', 'forever under 10 employees', 'var(--app-billy)'],
      ],
      kicker: '30-day free trial on every plan. Public pricing on every page. ',
      kickerBold: 'Up to 80% cheaper.',
      stat: { big: '−80%', small: 'vs Culture Amp, Lattice, 15Five, without losing features' },
      accent: 'var(--app-clappy)',
      accentBg: 'var(--app-clappy-bg)',
      badIcon: '🔒',
    },
    {
      n: '03',
      chip: 'Time to value',
      bad: 'Months to set up. Weeks to use.',
      badLong: "The enterprise platforms are powerful, but they show it. Multi-week implementation. Mandatory onboarding calls. Custom configuration sessions. Admin UIs with 47 tabs. By the time you're \"live,\" your team has lost interest.",
      fix: 'Install in 60 seconds. Default templates that work on day one.',
      bullets: [
        ['One-click', 'install via Slack or Teams marketplace', 'var(--app-alfy)'],
        ['Templates', 'that actually work, no consulting required', 'var(--app-stany)'],
        ['One-click', 'most config. No 47-tab admin UI.', 'var(--app-linky)'],
        ['No', 'mandatory onboarding call. Self-serve from day one.', 'var(--app-palmy)'],
      ],
      kicker: 'Your first survey can go out before lunch. ',
      kickerBold: 'Your first kudo, before this paragraph ends.',
      stat: { big: '60s', small: 'from clicking install to your first kudo in #general' },
      accent: 'var(--app-clappy)',
      accentBg: 'var(--app-clappy-bg)',
      badIcon: '⏳',
    },
  ];
  const p = problems[active];

  // ─── MOBILE: flat stack, no scroll-jack, no tabs ───
  if (isMobile) {
    return (
      <section style={{ padding: '64px 20px', background: 'var(--bh-cream-deep)', borderTop: '1px solid var(--bh-line)', borderBottom: '1px solid var(--bh-line)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div className="bh-chip" style={{ marginBottom: 14 }}>🚪 Why teams leave Culture Amp & Lattice</div>
            <h2 style={{ fontSize: 'clamp(28px, 7vw, 40px)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
              The three things that{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>make HR platforms suck.</span>
            </h2>
            <p style={{ fontSize: 15, color: 'var(--bh-ink-mute)', marginTop: 14, lineHeight: 1.5 }}>
              If you've used (or shopped for) Culture Amp, Lattice, 15Five or Officevibe, this will sound familiar.
            </p>
          </div>

          <div style={{ display: 'grid', gap: 18 }}>
            {problems.map((pr) => (
              <article key={pr.n} style={{
                borderRadius: 'var(--bh-r-xl)',
                border: '1.5px solid var(--bh-line)',
                boxShadow: 'var(--bh-shadow-sm)',
                overflow: 'hidden',
              }}>
                {/* PROBLEM half */}
                <div style={{ background: 'var(--bh-coral-50)', padding: '20px 20px 28px' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    background: '#fff', borderRadius: 999, padding: '5px 13px', marginBottom: 16,
                  }}>
                    <span style={{ color: 'var(--bh-coral-600)', fontWeight: 800, fontSize: 11, lineHeight: 1 }}>✕</span>
                    <span style={{ color: 'var(--bh-coral-600)', fontWeight: 700, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                      Problem · {pr.chip}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span style={{
                      width: 44, height: 44, borderRadius: 12, background: '#fff',
                      border: '1px solid var(--bh-coral-100)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, flexShrink: 0,
                    }}>{pr.badIcon}</span>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15, color: 'var(--bh-coral-600)', margin: '2px 0 8px' }}>
                        {pr.bad}
                      </h3>
                      <p style={{ fontSize: 14, color: 'var(--bh-ink-soft)', lineHeight: 1.5, margin: 0 }}>
                        {pr.badLong}
                      </p>
                    </div>
                  </div>
                </div>

                {/* SOLUTION half */}
                <div style={{ background: 'var(--app-clappy-bg)', padding: '30px 20px 24px', position: 'relative' }}>
                  {/* arrow divider straddling the boundary */}
                  <div style={{
                    position: 'absolute', top: -17, left: 22,
                    width: 34, height: 34, borderRadius: '50%',
                    background: '#fff', border: '1.5px solid var(--app-clappy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="var(--app-clappy)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="3" x2="8" y2="13" />
                      <polyline points="3.5,8.5 8,13 12.5,8.5" />
                    </svg>
                  </div>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    background: '#fff', borderRadius: 999, padding: '5px 13px', marginBottom: 14,
                  }}>
                    <span style={{ color: 'var(--app-clappy)', fontWeight: 800, fontSize: 11, lineHeight: 1 }}>✓</span>
                    <span style={{ color: 'var(--app-clappy)', fontWeight: 700, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                      Solution · BuddiesHR
                    </span>
                  </div>
                  <h4 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25, color: 'var(--bh-ink)', margin: '0 0 16px' }}>
                    {pr.fix}
                  </h4>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {pr.bullets.map(([k, v], i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, lineHeight: 1.45 }}>
                        <span style={{
                          width: 20, height: 20, borderRadius: '50%',
                          background: '#fff', border: '1.5px solid var(--app-clappy)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, marginTop: 1,
                        }}>
                          <svg viewBox="0 0 14 14" width="9" height="9" fill="none" stroke="var(--app-clappy)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="2.5,7.5 5.5,10.5 11.5,3.5" />
                          </svg>
                        </span>
                        <div>
                          <strong style={{ color: 'var(--bh-ink)', fontWeight: 700 }}>{k}</strong>{' '}
                          <span style={{ color: 'var(--bh-ink-soft)' }}>{v}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* stat */}
                  <div style={{
                    marginTop: 18, padding: '16px 18px', background: '#fff',
                    border: '1.5px solid var(--app-clappy)', borderRadius: 'var(--bh-r-md)',
                    display: 'flex', alignItems: 'center', gap: 16,
                  }}>
                    <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--app-clappy)', lineHeight: 1, flexShrink: 0 }}>
                      {pr.stat.big}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--bh-ink-soft)', lineHeight: 1.4 }}>{pr.stat.small}</div>
                  </div>

                  <p style={{ margin: '16px 0 0', fontSize: 13.5, color: 'var(--bh-ink-soft)', lineHeight: 1.5 }}>
                    {pr.kicker}<strong style={{ color: 'var(--bh-ink)' }}>{pr.kickerBold}</strong>
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} style={{ background: 'var(--bh-cream-deep)', borderTop: '1px solid var(--bh-line)', borderBottom: '1px solid var(--bh-line)', position: 'relative', height: '260vh' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '32px 40px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32, maxWidth: 780, marginLeft: 'auto', marginRight: 'auto' }}>
          <div className="bh-chip" style={{ marginBottom: 16 }}>🚪 Why teams leave Culture Amp & Lattice</div>
          <h2 style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', letterSpacing: '-0.03em', lineHeight: 1.04, margin: 0 }}>
            The three things that<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>make HR platforms suck.</span>
          </h2>
          <p style={{ fontSize: 17, color: 'var(--bh-ink-mute)', marginTop: 18, lineHeight: 1.55 }}>
            If you've used (or shopped for) Culture Amp, Lattice, 15Five or Officevibe, this will sound familiar.
          </p>
        </div>
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* card */}
        <div style={{
          background: '#fff', borderRadius: 'var(--bh-r-xl)',
          border: '1.5px solid var(--bh-line)',
          boxShadow: 'var(--bh-shadow-md)',
          overflow: 'hidden',
        }}>
          {/* tabs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid var(--bh-line)' }}>
            {problems.map((pr, i) => {
              const on = i === active;
              return (
                <button key={pr.n} onClick={() => setActive(i)} style={{
                  position: 'relative',
                  appearance: 'none', border: 'none', cursor: 'pointer',
                  padding: '22px 24px', textAlign: 'left',
                  background: on ? '#fff' : 'var(--bh-cream-deep)',
                  borderBottom: '3px solid transparent',
                  borderRight: i < 2 ? '1px solid var(--bh-line)' : 'none',
                  transition: 'all .2s',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  {/* scroll-progress fill, sits exactly over the bottom border */}
                  <span
                    aria-hidden="true"
                    ref={(el) => { barRefs.current[i] = el; }}
                    style={{
                      position: 'absolute', left: 0, bottom: -3, height: 3,
                      background: pr.accent, borderRadius: 2,
                    }}
                  />
                  <span style={{
                    width: 38, height: 38, borderRadius: 12,
                    background: on ? pr.accentBg : 'rgba(31,27,22,.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, transition: 'all .2s',
                  }}>{pr.badIcon}</span>
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: on ? pr.accent : 'var(--bh-ink-mute)', fontWeight: 700 }}>
                      {pr.n} · {pr.chip}
                    </div>
                    <div style={{ fontSize: 15, color: 'var(--bh-ink)', fontWeight: 600, marginTop: 2 }}>{pr.bad}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* body */}
          <div key={active} style={{
            display: 'grid', gridTemplateColumns: '1.2fr 1fr',
            animation: 'bh-tab-fade .35s cubic-bezier(.3,1,.4,1) both',
          }}>
            {/* problem */}
            <div style={{ padding: 40, borderRight: '1px solid var(--bh-line)' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(232,93,117,.12)', color: 'var(--bh-coral-600)',
                fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                marginBottom: 16,
              }}>✕ Problem</div>
              <h3 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.15, color: 'var(--bh-ink)', margin: 0 }}>
                {p.bad}
              </h3>
              <p style={{ fontSize: 15, color: 'var(--bh-ink-soft)', lineHeight: 1.55, margin: '14px 0 22px' }}>
                {p.badLong}
              </p>
              <ProblemEvidence active={active} />
            </div>
            {/* fix */}
            <div style={{ padding: 40, background: p.accentBg, position: 'relative' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(255,255,255,.7)', color: p.accent,
                fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                marginBottom: 16,
              }}>✓ How we fix it</div>
              <h4 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25, color: 'var(--bh-ink)', margin: 0 }}>
                {p.fix}
              </h4>
              <div style={{ marginTop: 20, display: 'grid', gap: 14 }}>
                {p.bullets.map(([k, v], i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14.5, lineHeight: 1.5 }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#fff', border: `1.5px solid ${p.accent}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, marginTop: 1,
                    }}>
                      <svg viewBox="0 0 14 14" width="10" height="10" fill="none" stroke={p.accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2.5,7.5 5.5,10.5 11.5,3.5" />
                      </svg>
                    </span>
                    <div>
                      <strong style={{ color: 'var(--bh-ink)', fontWeight: 700 }}>{k}</strong>{' '}
                      <span style={{ color: 'var(--bh-ink-soft)' }}>{v}</span>
                    </div>
                  </div>
                ))}
              </div>
              {p.stat.barPct ? (
                <div style={{
                  marginTop: 22, padding: '18px 20px', background: '#fff',
                  border: `1.5px solid ${p.accent}`,
                  borderRadius: 'var(--bh-r-md)',
                }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink-mute)', letterSpacing: '.04em', marginBottom: 12, textTransform: 'uppercase' }}>
                    {p.stat.barLabel}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontFamily: 'var(--bh-font-display)', fontSize: 44, fontWeight: 700, color: p.accent, letterSpacing: '-0.04em', lineHeight: 1 }}>{p.stat.big}</span>
                    <span style={{ fontSize: 13, color: 'var(--bh-ink-soft)' }}>{p.stat.small}</span>
                  </div>
                  <div style={{
                    height: 14, borderRadius: 999,
                    background: 'rgba(31,27,22,.08)',
                    overflow: 'hidden', position: 'relative',
                  }}>
                    <div style={{
                      width: `${p.stat.barPct}%`, height: '100%',
                      background: p.accent,
                      borderRadius: 999,
                    }} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--bh-ink-mute)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              ) : (
                <div style={{
                  marginTop: 22, padding: '16px 20px', background: '#fff',
                  border: `1.5px solid ${p.accent}`,
                  borderRadius: 'var(--bh-r-md)',
                  display: 'flex', alignItems: 'center', gap: 18,
                }}>
                  <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em', color: p.accent, lineHeight: 1, flexShrink: 0 }}>
                    {p.stat.big}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--bh-ink-soft)', lineHeight: 1.45 }}>{p.stat.small}</div>
                </div>
              )}
              <p style={{ marginTop: 16, fontSize: 13.5, color: 'var(--bh-ink-soft)', lineHeight: 1.5, margin: '16px 0 0' }}>
                {p.kicker}<strong style={{ color: 'var(--bh-ink)' }}>{p.kickerBold}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
      <style>{`@keyframes bh-tab-fade {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }`}</style>
    </section>
  );
}

// ═══ "NATIVE, BUT NOT JUST A BOT" ═══
function NativeNotBot({ platform }) {
  const nativeIn =
    platform === 'slack'
      ? 'Slack'
      : platform === 'teams'
      ? 'Microsoft Teams'
      : 'Slack and Teams';
  return (
    <section className="bh-section" style={{ maxWidth: 1360, margin: '0 auto' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', padding: '0 24px' }}>
        <div className="bh-chip" style={{ marginBottom: 16 }}>🤖 + 🖥️ Native, but not just a bot</div>
        <h2 style={{ fontSize: 'clamp(32px, 4.2vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          Native in {nativeIn}.<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>Backed by a full HR platform.</span>
        </h2>
        <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
          {platform !== 'teams' && <LaurelBadge eyebrow="Featured by" label="Slack" />}
          {platform !== 'slack' && <LaurelBadge eyebrow="Top Rated App" label="Microsoft Teams" />}
        </div>
        <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 20, lineHeight: 1.55 }}>
          Living in Slack/Teams is what gets your team to use it, but HR needs more than a chatbot. Every BuddiesHR module comes with a real web platform: dashboards, analytics, segmentation, templates, org chart, review cycles, exportable reports.
        </p>
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 18, fontSize: 15, fontWeight: 700 }}>
          <Link href="/slack" style={{ color: 'var(--bh-ink)' }}>
            See BuddiesHR for Slack <span className="bh-arrow">→</span>
          </Link>
          <Link href="/microsoft-teams" style={{ color: 'var(--bh-ink)' }}>
            See BuddiesHR for Microsoft Teams <span className="bh-arrow">→</span>
          </Link>
        </div>
        <div className="bh-grid-3" style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, textAlign: 'center' }}>
          {[
            ['💬', 'Employees', 'stay in the chat tools they already love.'],
            ['📊', 'HR admins', 'get the proper People Platform they need.'],
            ['🔗', 'Same data', 'flows seamlessly between both surfaces.'],
          ].map(([icon, title, desc]) => (
            <div key={title} style={{
              background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-md)',
              border: '1.5px solid var(--bh-line)', padding: '22px 18px',
              boxShadow: 'var(--bh-shadow-sm)',
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{icon}</div>
              <div style={{ fontSize: 15, lineHeight: 1.5 }}>
                <strong>{title}</strong>{' '}
                <span style={{ color: 'var(--bh-ink-soft)' }}>{desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: 1280, margin: '40px auto 0' }}>
        <NativeVisual />
      </div>
    </section>
  );
}

function NativeBotVisual() {
  return (
    <div style={{ position: 'relative', height: 460 }}>
      {/* connecting line */}
      <svg style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }} viewBox="0 0 400 460" preserveAspectRatio="none">
        <path d="M 80 110 C 200 110, 200 350, 320 350" stroke="var(--bh-coral-400)" strokeWidth="2" strokeDasharray="4 6" fill="none" opacity="0.55" />
      </svg>
      {/* Slack bubble (top-left) */}
      <div style={{
        position: 'absolute', top: 20, left: 0, width: 240,
        background: '#fff', borderRadius: 14,
        border: '1.5px solid var(--bh-line)',
        boxShadow: 'var(--bh-shadow-md)',
        padding: 14, zIndex: 2,
        transform: 'rotate(-2deg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: '#4A154B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>◆</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--bh-ink)' }}>#general</div>
          <span style={{ fontSize: 9, padding: '1px 4px', background: 'rgba(31,27,22,.08)', color: 'var(--bh-ink-mute)', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em', marginLeft: 'auto' }}>SLACK</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--app-pulsy-bg)', flexShrink: 0, overflow: 'hidden' }}><PulsyLogo /></div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--bh-ink)' }}>Pulsy <span style={{ fontSize: 8, padding: '1px 3px', background: 'rgba(31,27,22,.08)', borderRadius: 2, color: 'var(--bh-ink-mute)' }}>APP</span></div>
            <div style={{ fontSize: 11, color: 'var(--bh-ink-soft)', marginTop: 2, lineHeight: 1.35 }}>How's this week going?</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              <span style={{ padding: '2px 7px', background: 'var(--app-pulsy-bg)', borderRadius: 4, fontSize: 10, fontWeight: 600, color: 'var(--app-pulsy)' }}>😍</span>
              <span style={{ padding: '2px 7px', background: 'var(--bh-cream)', borderRadius: 4, fontSize: 10, fontWeight: 600, color: 'var(--bh-ink-soft)' }}>🙂</span>
              <span style={{ padding: '2px 7px', background: 'var(--bh-cream)', borderRadius: 4, fontSize: 10, fontWeight: 600, color: 'var(--bh-ink-soft)' }}>😕</span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', top: 90, left: 90, fontSize: 11, color: 'var(--bh-ink-mute)', fontWeight: 600, fontStyle: 'italic', zIndex: 2, transform: 'rotate(-2deg)' }}>
        ↑ where employees live
      </div>

      {/* Web platform window (bottom-right) */}
      <div style={{
        position: 'absolute', bottom: 0, right: 0, width: 320,
        background: '#fff', borderRadius: 14,
        border: '1.5px solid var(--bh-line)',
        boxShadow: 'var(--bh-shadow-md)',
        overflow: 'hidden', zIndex: 2,
        transform: 'rotate(1.5deg)',
      }}>
        <div style={{ padding: '8px 12px', background: 'var(--bh-cream-deep)', borderBottom: '1px solid var(--bh-line)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF5F57' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FEBC2E' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#28C840' }} />
          <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--bh-ink-mute)', fontWeight: 600 }}>app.buddieshr.com/employee-survey</div>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700 }}>Pulsy Dashboard</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: 'var(--bh-ink)' }}>Engagement, Q4</div>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[['eNPS', '+42', 'var(--app-palmy)'], ['Resp', '84%', 'var(--app-pulsy)'], ['Trend', '↑12', 'var(--app-clappy)']].map(([k, v, c]) => (
              <div key={k} style={{ padding: 8, background: 'var(--bh-cream)', borderRadius: 8, border: '1px solid var(--bh-line)' }}>
                <div style={{ fontSize: 9, color: 'var(--bh-ink-mute)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c, fontFamily: 'var(--bh-font-display)', letterSpacing: '-0.02em', marginTop: 2 }}>{v}</div>
              </div>
            ))}
          </div>
          {/* tiny chart */}
          <div style={{ marginTop: 12, height: 60, position: 'relative' }}>
            <svg viewBox="0 0 280 60" style={{ width: '100%', height: '100%' }}>
              <path d="M 0 50 L 30 42 L 60 38 L 90 30 L 120 32 L 150 22 L 180 18 L 210 14 L 240 10 L 280 4" stroke="var(--app-pulsy)" strokeWidth="2" fill="none" strokeLinecap="round" />
              <path d="M 0 50 L 30 42 L 60 38 L 90 30 L 120 32 L 150 22 L 180 18 L 210 14 L 240 10 L 280 4 L 280 60 L 0 60 Z" fill="var(--app-pulsy-bg)" opacity="0.6" />
            </svg>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: 200, right: 100, fontSize: 11, color: 'var(--bh-ink-mute)', fontWeight: 600, fontStyle: 'italic', zIndex: 2, transform: 'rotate(1.5deg)' }}>
        ↓ where HR runs the program
      </div>
    </div>
  );
}

// ═══ "WHERE WORK HAPPENS" ═══
function WhereWork() {
  const tiles = [
    { name: 'Slack', tag: 'Slack-native', desc: 'One-click install. Coffee chats, kudos, surveys, birthdays, reviews, all from Slack.', bg: '#4A154B', icon: '◆', href: '/slack' },
    { name: 'Microsoft Teams', tag: 'Teams-native', desc: 'Same power, same simplicity, inside Teams. (New in 2026.)', bg: '#6264A7', icon: '▲', href: '/microsoft-teams' },
    { name: 'Web platform', tag: 'For HR admins', desc: 'Dashboards, analytics, templates, org chart, review cycles, reports.', bg: 'var(--bh-ink)', icon: '▢' },
  ];
  return (
    <section className="bh-section" style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 56, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
        <div className="bh-chip" style={{ marginBottom: 16 }}>📍 Where work already happens</div>
        <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          Your team already lives<br/>in Slack or Teams.
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}> So do we.</span>
        </h2>
        <p style={{ fontSize: 17, color: 'var(--bh-ink-mute)', marginTop: 16, lineHeight: 1.55 }}>
          Every HR tool claims to be "easy to adopt." Most end up as a 12% participation rate and a dashboard nobody opens. BuddiesHR works differently, employees participate from the tool they already have open all day.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        {tiles.map(t => {
          const card = (
            <div key={t.name} style={{
              background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-lg)',
              border: '1.5px solid var(--bh-line)', padding: 28,
              boxShadow: 'var(--bh-shadow-sm)', height: '100%',
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: 12,
                background: t.bg, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, fontWeight: 700, marginBottom: 18,
              }}>{t.icon}</div>
              <div style={{ fontSize: 13, color: 'var(--bh-sun-500)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>{t.tag}</div>
              <h3 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>{t.name}</h3>
              <p style={{ fontSize: 15, color: 'var(--bh-ink-soft)', lineHeight: 1.5 }}>{t.desc}</p>
              {t.href && (
                <div style={{ fontSize: 14, color: 'var(--bh-ink)', fontWeight: 700, marginTop: 14 }}>
                  See BuddiesHR for {t.name} <span className="bh-arrow">→</span>
                </div>
              )}
            </div>
          );
          return t.href ? (
            <Link key={t.name} href={t.href} style={{ textDecoration: 'none', color: 'inherit' }}>
              {card}
            </Link>
          ) : card;
        })}
      </div>
    </section>
  );
}

// ═══ 4 PILLARS (the anchor of the page) ═══
const PILLARS = [
  { id: 'measure',  label: 'Measure',  emoji: '📊', color: 'var(--app-pulsy)',  bg: 'var(--app-pulsy-bg)',
    headline: 'Know how your team really feels.',
    apps: ['pulsy', 'perf', 'clappy'] },
  { id: 'engage',   label: 'Engage',   emoji: '💛', color: 'var(--app-billy)',  bg: 'var(--app-billy-bg)',
    headline: 'Build culture in the small moments.',
    apps: ['clappy', 'alfy', 'billy'] },
  { id: 'perform',  label: 'Perform',  emoji: '🎯', color: 'var(--app-perf)',   bg: 'var(--app-perf-bg)',
    headline: "Reviews people don't hate.",
    apps: ['perf', 'clappy', 'stany'] },
  { id: 'automate', label: 'Automate', emoji: '⚙️', color: 'var(--app-palmy)',  bg: 'var(--app-palmy-bg)',
    headline: "Take the admin off HR's plate.",
    apps: ['linky', 'palmy', 'stany'] },
];

const APP_ONE_LINER = {
  pulsy:  'Surveys, polls, eNPS and pulse checks, delivered in Slack or Teams.',
  clappy: 'Peer-to-peer recognition tied to your company values.',
  alfy:   'Scheduled coffee chats that actually connect people.',
  billy:  'Birthdays and work anniversaries celebrated without HR lifting a finger.',
  perf:   'Performance reviews, 360s and check-ins. Designed for SMBs, powered by AI, priced 3× below the alternatives.',
  linky:  'Org chart and employee directory that updates itself.',
  palmy:  'PTO and vacation tracking, right in Slack or Teams.',
  stany:  'Async standups that replace another 15-minute meeting.',
};

function AutomateVisual({ p }) {
  return <PillarCanvas><AutomateVisualArt /></PillarCanvas>;
}

function EngageVisual({ p }) {
  return <PillarCanvas><EngageVisualArt /></PillarCanvas>;
}

function MeasureVisual({ p }) {
  return <PillarCanvas><MeasureVisualArt /></PillarCanvas>;
}

function PerformVisual({ p }) {
  return <PillarCanvas><PerformVisualArt /></PillarCanvas>;
}

// Wrapper for the vector pillar visuals: bleeds 16px into the row's side
// padding on desktop (reset on mobile via .bh-pillar-img / .bh-pillar-canvas).
function PillarCanvas({ children }) {
  return (
    <div className="bh-pillar-img bh-pillar-canvas" style={{ width: '100%', margin: '-40px 0' }}>
      <div style={{ width: 'calc(100% + 16px)' }}>{children}</div>
    </div>
  );
}

function Pillars() {
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  const NUM = { measure: '01', engage: '02', perform: '03', automate: '04' };
  const SUB = {
    measure: 'Pulse surveys, eNPS, performance signals, and the values your team actually lives day-to-day, measured in one place. People answer in Slack or Teams in under 30 seconds, so you get the full picture instead of guessing from a 22% response rate.',
    engage: 'The small moments that make people stay. Kudos tied to your company values, automated birthdays and work anniversaries, and scheduled coffee chats that quietly turn coworkers into actual buddies. No HR ops time required, it runs itself in the channels your team already opens every day.',
    perform: 'Reviews, 360s, weekly check-ins and goal tracking, designed for SMBs, powered by AI, and priced 3× below Lattice or 15Five. Pair it with Clappy and recognition data flows straight into review cycles, so feedback is rooted in what people actually shipped, not what they remembered in the last two weeks.',
    automate: "The admin work that quietly eats HR's calendar, PTO requests, org chart updates, async standups, onboarding checklists, handled where people already work. Approvals happen in a Slack thread. The org chart updates itself from your HRIS. Standups replace another 15-minute meeting. Your HR team gets their afternoons back.",
  };
  const CASE = {
    measure:  { company: 'Roo',   text: 'rolled out eNPS across a 100% remote team', href: '/case-studies/roo' },
    engage:   { company: 'Sched', text: 'built a stronger culture with kudos and celebrations', href: '/case-studies/sched' },
    perform:  { company: 'Roo',   text: 'scaled recognition and async check-ins', href: '/case-studies/roo' },
    automate: { company: 'Sched', text: 'runs people ops without a full-time HR team', href: '/case-studies/sched' },
  };
  return (
    <section id="pillars" style={{ background: 'var(--bh-cream)', borderTop: '1px solid var(--bh-line)', borderBottom: '1px solid var(--bh-line)' }}>
      <div className="bh-section" style={{ background: '#fff', paddingBottom: 32 }}>
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
          <div className="bh-chip" style={{ marginBottom: 16 }}>🏛️ The 4 pillars</div>
          <h2 style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Four pillars,<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>one platform.</span>
          </h2>
          <p style={{ fontSize: 17, color: 'var(--bh-ink-mute)', marginTop: 14, lineHeight: 1.5 }}>
            From the moment you ask "how's the team?" <br className="bh-break-md" />to the moment you sign off on PTO, BuddiesHR is there.
          </p>
        </div>
      </div>
      {PILLARS.map((p, i) => (
        <div key={p.id} style={{
          background: i % 2 === 0 ? '#fff' : 'var(--bh-cream-deep)',
          borderTop: i === 0 ? 'none' : '1px solid var(--bh-line)',
          borderBottom: i === PILLARS.length - 1 ? '1px solid var(--bh-line)' : 'none',
        }}>
          <div className={(p.id === 'automate' || p.id === 'engage' || p.id === 'measure' || p.id === 'perform') ? 'bh-stack bh-pillar-row' : 'bh-stack'} style={{ maxWidth: (p.id === 'automate' || p.id === 'engage' || p.id === 'measure' || p.id === 'perform') ? 1480 : 1180, margin: '0 auto', padding: (p.id === 'automate' || p.id === 'engage' || p.id === 'measure' || p.id === 'perform') ? '88px 16px 88px 40px' : '96px 40px', display: 'grid', gridTemplateColumns: (p.id === 'automate' || p.id === 'engage' || p.id === 'measure' || p.id === 'perform') ? undefined : '1.1fr 1fr', gap: (p.id === 'automate' || p.id === 'engage' || p.id === 'measure' || p.id === 'perform') ? 24 : 56, alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                <span style={{
                  fontFamily: 'Boldonse, serif', fontSize: 14,
                  color: p.color, letterSpacing: '-0.01em',
                  background: p.bg, padding: '4px 10px', borderRadius: 8,
                  border: `1.5px solid ${p.color}`,
                  lineHeight: 1,
                }}>{NUM[p.id]}</span>
                <span style={{ fontFamily: 'Boldonse, serif', fontSize: 28, color: p.color, letterSpacing: '-0.02em', textTransform: 'lowercase', lineHeight: 1 }}>{p.label.toLowerCase()}</span>
              </div>
              <h3 style={{ fontSize: 'clamp(36px, 4vw, 50px)', letterSpacing: '-0.03em', lineHeight: 1.05, marginBottom: 18 }}>{p.headline}</h3>
              <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', lineHeight: 1.55, marginBottom: 24, maxWidth: 520 }}>{SUB[p.id]}</p>
              <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8 }}>
                {p.apps.map(aid => {
                  const app = byId[aid];
                  return (
                    <span key={aid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: '#fff', border: '1.5px solid var(--bh-line)',
                      borderRadius: 999, padding: '6px 14px 6px 6px',
                      fontSize: 14, fontWeight: 600,
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                      <span style={{ width: 28, height: 28 }}>{app && <app.Logo />}</span>
                      {app ? app.name : aid}
                    </span>
                  );
                })}
              </div>
              {CASE[p.id] && (
                <Link href={CASE[p.id].href} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  marginTop: 22,
                  fontSize: 14.5, fontWeight: 500,
                  color: 'var(--bh-ink-soft)',
                  textDecoration: 'none',
                  alignSelf: 'flex-start',
                  width: 'fit-content',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: '50%',
                    background: p.bg, color: p.color,
                    fontSize: 11, fontWeight: 800,
                    border: `1.5px solid ${p.color}`,
                    flexShrink: 0,
                  }}>★</span>
                  <span>
                    See how <strong style={{ color: 'var(--bh-ink)', fontWeight: 700 }}>{CASE[p.id].company}</strong> {CASE[p.id].text}{' '}
                    <span style={{ color: p.color, fontWeight: 700 }}><span className="bh-arrow">→</span></span>
                  </span>
                </Link>
              )}
            </div>
            {p.id === 'automate' ? (
              <AutomateVisual p={p} />
            ) : p.id === 'engage' ? (
              <EngageVisual p={p} />
            ) : p.id === 'measure' ? (
              <MeasureVisual p={p} />
            ) : p.id === 'perform' ? (
              <PerformVisual p={p} />
            ) : (
            <div style={{
              background: p.bg, borderRadius: 'var(--bh-r-xl)',
              border: `1.5px solid ${p.color}`,
              padding: 32, position: 'relative', minHeight: 280,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'var(--bh-shadow-sm)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(p.apps.length, 3)}, 1fr)`, gap: 20 }}>
                {p.apps.map(aid => {
                  const app = byId[aid];
                  return (
                    <div key={aid} style={{ width: 120, height: 120, filter: 'drop-shadow(0 12px 22px rgba(31,27,22,.18))' }}>
                      {app && <app.Logo />}
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        </div>
      ))}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '56px 40px 100px' }}>
        {/* CTA strip */}
        <div style={{
          marginTop: 56,
          background: 'var(--bh-ink)',
          color: 'var(--bh-cream)',
          borderRadius: 'var(--bh-r-xl)',
          padding: '32px 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 32,
          flexWrap: 'wrap',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 95% 30%, rgba(255,184,77,.18), transparent 55%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', flex: '1 1 380px', minWidth: 0 }}>
            <h3 style={{ fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 500, letterSpacing: '-0.025em', lineHeight: 1.15, color: 'var(--bh-cream)' }}>
              Four pillars. <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-300)' }}>Where work already happens.</span>
            </h3>
            <p style={{ marginTop: 8, fontSize: 15, color: 'rgba(255,248,238,.7)', lineHeight: 1.5, maxWidth: 520 }}>
              Install in 60 seconds in Slack or Microsoft Teams. Pick the modules you need. Starting from $1 per app, Free under 10 employees.
            </p>
          </div>
          <div style={{ position: 'relative', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link href="/get-started" className="bh-btn primary on-dark">Get started (free) <span className="bh-arrow">→</span></Link>
            <Link href="/pricing" className="bh-btn secondary on-dark">See pricing</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══ MODULAR SECTION ═══
function Modular() {
  return (
    <section id="modules" className="bh-section" style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div className="bh-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 64, alignItems: 'center' }}>
        <div>
          <div className="bh-chip" style={{ marginBottom: 16 }}>🧩 Modular</div>
          <h2 style={{ fontSize: 'clamp(32px, 4.2vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Start with one app.<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>Add the next when you're ready.</span>
          </h2>
          <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 20, lineHeight: 1.55, maxWidth: 520 }}>
            Most People Platforms are all-or-nothing. You buy the full suite, pay for 7 things, and use 2. BuddiesHR is the opposite.
          </p>
          <div style={{ marginTop: 26, display: 'grid', gap: 14 }}>
            {[
              ['Each module stands alone.', 'Start with Clappy for recognition. Or Pulsy for surveys. Or Billy just to stop forgetting birthdays.'],
              ['Each module works together.', 'Install a second and they share data, recognitions feed into reviews, org chart feeds into surveys, and so on.'],
              ['Each module is independently priced.', '$1/user/month for any single app. $2/user/month for the full Culture OS suite. $3 if you want SimplePerf included. No bundling games.'],
            ].map(([t, d]) => (
              <div key={t} style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--bh-sun-400)', color: 'var(--bh-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 800, marginTop: 3 }}>✓</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{t}</div>
                  <div style={{ color: 'var(--bh-ink-mute)', fontSize: 14, lineHeight: 1.5, marginTop: 2 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
          <Link href="/get-started" className="bh-btn primary" style={{ marginTop: 30 }}>Pick your first module <span className="bh-arrow">→</span></Link>
        </div>
        <ModularGrid />
      </div>
    </section>
  );
}

function ModularGrid() {
  const [picked, setPicked] = useState(new Set(['billy']));
  const toggle = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };
  // Pricing:
  //   0 apps → $0
  //   1 app  → $1
  //   2+ apps without SimplePerf → $2 (full suite except perf)
  //   2+ apps including SimplePerf → $3
  //   SimplePerf only → $1
  const hasPerf = picked.has('perf');
  const n = picked.size;
  let totalNum = 0;
  if (n === 0) totalNum = 0;
  else if (n === 1) totalNum = 1;
  else totalNum = hasPerf ? 3 : 2;
  const total = totalNum.toFixed(2);
  return (
    <div style={{
      background: '#fff', borderRadius: 'var(--bh-r-xl)',
      border: '1.5px solid var(--bh-line)',
      boxShadow: 'var(--bh-shadow-md)',
      padding: 28,
    }}>
      <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700, marginBottom: 4 }}>Build your suite</div>
      <div style={{ fontSize: 16, color: 'var(--bh-ink-soft)', marginBottom: 18 }}>$1 one app · $2 any bundle · $3 with SimplePerf.</div>
      <div className="bh-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {APPS.map(app => {
          const on = picked.has(app.id);
          return (
            <button key={app.id} onClick={() => toggle(app.id)}
              style={{
                background: on ? app.bg : 'var(--bh-cream)',
                border: '1.5px solid', borderColor: on ? app.color : 'var(--bh-line)',
                borderRadius: 'var(--bh-r-md)',
                padding: '14px 10px', cursor: 'pointer',
                transition: 'all .2s cubic-bezier(.3,1.3,.4,1)',
                transform: on ? 'translateY(-3px) scale(1.02)' : 'none',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
              }}>
              <div style={{ width: 56, height: 56, filter: on ? 'drop-shadow(0 4px 8px rgba(31,27,22,.15))' : 'grayscale(.3) opacity(.85)', transition: 'filter .2s' }}>
                <app.Logo />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8, color: on ? app.color : 'var(--bh-ink-soft)' }}>{app.name}</div>
            </button>
          );
        })}
      </div>
      <div style={{
        marginTop: 20, padding: 18, background: 'var(--bh-cream)',
        borderRadius: 'var(--bh-r-md)', border: '1px solid var(--bh-line)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>
            {n === 0 ? 'No apps selected'
              : n === 1 ? '1 app'
              : hasPerf ? `${n} apps (incl. SimplePerf)` : `${n} apps`}
          </div>
          <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--bh-ink)' }}>
            ${total}<span style={{ fontSize: 15, color: 'var(--bh-ink-mute)', fontWeight: 400 }}>/user/month</span>
          </div>
        </div>
        <Link href="/get-started" className="bh-btn primary" style={{ fontSize: 14, padding: '10px 18px' }}>Start free <span className="bh-arrow">→</span></Link>
      </div>
    </div>
  );
}

// ═══ TRY IT, DON'T DEMO IT ═══
// Reframed around flexibility + transparency.
function TryItDontDemo() {
  const items = [
    { icon: '🔍', big: 'Public', label: 'pricing',   body: 'on every page. No “contact sales” to find out what it costs.' },
    { icon: '🧩', big: 'Pick',   label: 'what you need', body: '$1 per app or $2 for the full suite. Add or drop modules anytime.' },
    { icon: '🔓', big: 'No',     label: 'lock-in',   body: 'Monthly or annual. Cancel from settings. Export your data anytime.' },
  ];
  return (
      <section id="pricing" className="bh-section" style={{ background: 'var(--bh-ink)', color: 'var(--bh-cream)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 60, right: 80, width: 140, opacity: .55 }} className="bh-float"><ClappyLogo /></div>
      <div style={{ position: 'absolute', bottom: 60, left: 80, width: 120, opacity: .5 }} className="bh-float2"><PulsyLogo /></div>
      <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', maxWidth: 1040, margin: '0 auto 48px' }}>
          <div className="bh-chip" style={{ background: 'var(--bh-sun-400)', color: 'var(--bh-ink)', borderColor: 'var(--bh-sun-500)', marginBottom: 22 }}>👀 Try it, don't demo it</div>
          <h2 style={{ fontSize: 'clamp(34px, 5vw, 62px)', letterSpacing: '-0.035em', lineHeight: 1.04, color: 'var(--bh-cream)' }}>
            See it live in 60 seconds.<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-300)' }}>No quote needed, no annual lock-in.</span>
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,248,238,.75)', marginTop: 22, lineHeight: 1.55, maxWidth: 720, margin: '22px auto 0' }}>
            Most HR platforms gate everything behind a quote, a contract, and a quarterly business review. We don't. Pick the apps you want, change your mind whenever, leave whenever. The whole thing is on this page.
          </p>
        </div>

        <div className="bh-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {items.map((s, i) => (
            <div key={i} style={{
              background: 'rgba(255,248,238,.06)',
              border: '1px solid rgba(255,248,238,.18)',
              borderRadius: 'var(--bh-r-lg)',
              padding: 28,
            }}>
              <div style={{ fontSize: 26, marginBottom: 12 }}>{s.icon}</div>
              <div style={{ fontFamily: 'var(--bh-font-display)', fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1 }}>
                <span style={{ fontSize: 52, color: 'var(--bh-sun-300)' }}>{s.big}</span>
                <span style={{ fontSize: 16, color: 'rgba(255,248,238,.6)', marginLeft: 6 }}>{s.label}</span>
              </div>
              <div style={{ marginTop: 14, fontSize: 15, color: 'rgba(255,248,238,.78)', lineHeight: 1.5 }}>{s.body}</div>
            </div>
          ))}
        </div>

        <div className="bh-stack" style={{
          marginTop: 36,
          background: 'rgba(255,248,238,.04)',
          border: '1px dashed rgba(255,248,238,.22)',
          borderRadius: 'var(--bh-r-lg)',
          padding: '28px 32px',
          display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 24, alignItems: 'center',
        }}>
          <div style={{ fontSize: 36 }}>🛠️</div>
          <div style={{ fontSize: 16, color: 'rgba(255,248,238,.85)', lineHeight: 1.55 }}>
            Sign up with your Slack or Teams workspace. Install one app. Add another next month. Drop one the month after. <strong style={{ color: 'var(--bh-cream)' }}>Your bill follows you, not the other way around.</strong> 30-day free trial. Free forever under 10 employees.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link href="/get-started" className="bh-btn primary on-dark" style={{ fontSize: 14, padding: '10px 18px' }}>Start free <span className="bh-arrow">→</span></Link>
            <Link href="/pricing" className="bh-btn secondary on-dark" style={{ fontSize: 14, padding: '10px 18px' }}>See full pricing</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══ COMPARISON (teaser, short version) ═══
function Comparison() {
  // Each row: [label, BuddiesHR (winner), Culture Amp / Lattice]
  const rows = [
    { label: 'Starting price',           bh: '$1/user/mo',         them: '$9–11/user/mo' },
    { label: 'Free trial',               bh: '30 days, no card',   them: 'None, sales call required' },
    { label: 'Public pricing',           bh: { check: true, text: 'On every page' }, them: { x: true, text: 'Request a quote' } },
    { label: 'Time to first use',        bh: '60 seconds',         them: '4–8 weeks of implementation' },
    { label: 'Native in Slack & Teams',  bh: { check: true, text: 'End-to-end' }, them: 'Notifications only' },
  ];
  return (
    <section id="compare" className="bh-compare bh-section" style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div className="bh-chip" style={{ marginBottom: 16 }}>😅 They don't stand a chance</div>
        <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          BuddiesHR vs.<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>the alternatives.</span>
        </h2>
      </div>

      <div style={{
        background: '#fff', borderRadius: 'var(--bh-r-xl)',
        border: '1.5px solid var(--bh-line)', overflow: 'hidden',
        boxShadow: 'var(--bh-shadow-md)',
        position: 'relative',
      }}>
        {/* highlighted column ribbon */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: 'calc(33.33% + (33.33% - 16px) / 2)',
          transform: 'translateX(-50%)',
          width: 'calc(33.33% - 8px)',
          background: 'var(--bh-sun-100)',
          zIndex: 0,
          borderLeft: '1.5px solid var(--bh-sun-400)',
          borderRight: '1.5px solid var(--bh-sun-400)',
        }} />

        <table className="bh-compare-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, position: 'relative', zIndex: 1 }}>
          <thead>
            <tr>
              <th style={{ padding: '20px 24px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.08em', borderBottom: '1.5px solid var(--bh-line)' }}></th>
              <th style={{ padding: '20px 24px', textAlign: 'center', borderBottom: '1.5px solid var(--bh-sun-400)' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <BHWordmark height={20} />
                </div>
              </th>
              <th style={{ padding: '20px 24px', textAlign: 'center', fontWeight: 600, fontSize: 15, color: 'var(--bh-ink-mute)', borderBottom: '1.5px solid var(--bh-line)', fontFamily: 'var(--bh-font-display)', letterSpacing: '-0.01em' }}>
                Culture Amp / Lattice
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--bh-line)' }}>
                <td style={{ padding: '20px 24px', fontWeight: 600, color: 'var(--bh-ink)', fontSize: 15 }}>{r.label}</td>
                <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                  <CompCell value={r.bh} winner />
                </td>
                <td style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--bh-ink-mute)' }}>
                  <CompCell value={r.them} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 28, textAlign: 'center', lineHeight: 1.55, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
        Same features. Roughly 80% cheaper. <strong style={{ color: 'var(--bh-ink)' }}>Live in your team's chat tools by lunchtime.</strong>
      </p>

      <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link href="/get-started" className="bh-btn primary">Get started now <span className="bh-arrow">→</span></Link>
        <Link href="/alternatives" className="bh-btn ghost">See the full comparison <span className="bh-arrow">→</span></Link>
      </div>
    </section>
  );
}

function CompCell({ value, winner }) {
  if (typeof value === 'string') {
    return (
      <span style={{
        fontWeight: winner ? 700 : 500,
        color: winner ? 'var(--bh-ink)' : 'var(--bh-ink-mute)',
        fontFamily: winner ? 'var(--bh-font-display)' : 'var(--bh-font-body)',
        fontSize: winner ? 17 : 15,
        letterSpacing: winner ? '-0.01em' : 'normal',
      }}>{value}</span>
    );
  }
  if (value && value.check) {
    return (
      <span className="bh-cmp-iconcell" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, color: 'var(--bh-ink)', fontFamily: 'var(--bh-font-display)', fontSize: 17, letterSpacing: '-0.01em' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          background: 'var(--app-palmy)', color: '#fff',
          fontSize: 13, fontWeight: 800,
        }}>✓</span>
        {value.text}
      </span>
    );
  }
  if (value && value.x) {
    return (
      <span className="bh-cmp-iconcell" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--bh-ink-mute)' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          background: 'rgba(31,27,22,.06)', color: 'var(--bh-coral-400)',
          fontSize: 13, fontWeight: 800,
        }}>✕</span>
        {value.text}
      </span>
    );
  }
  return null;
}

// ═══ TESTIMONIALS ═══
function G2RatingStrip({ centered = false }) {
  const badges = [
    { label: 'Grid Leader', src: '/img/g2-modern-gridleader.png' },
    { label: 'High Performer', src: '/img/g2-modern-highperformer.png' },
    { label: 'Best Results', src: '/img/g2-modern-bestresults.png' },
  ];
  return (
    <div
      className={`bh-g2-strip${centered ? ' centered' : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}
    >
      {/* G2 rating cluster */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div aria-hidden style={{
          width: 42, height: 42, borderRadius: '50%',
          background: 'var(--bh-coral-400)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 17, letterSpacing: '-0.02em',
          fontFamily: 'var(--bh-font-display)',
          boxShadow: '0 2px 0 rgba(0,0,0,.06)',
        }}>G<sup style={{ fontSize: 11, marginLeft: 1 }}>2</sup></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', gap: 1 }} aria-label="Rated 4.8 out of 5">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} style={{ color: 'var(--bh-sun-400)', fontSize: 18, lineHeight: 1 }}>★</span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--bh-ink-mute)', fontWeight: 500 }}>Rated 4.8/5</div>
        </div>
      </div>
      {/* divider */}
      <div className="bh-g2-divider" style={{ width: 1, height: 36, background: 'var(--bh-line)' }} />
      {/* award badges */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {badges.map((b, i) => (
          <img
            key={i}
            src={b.src}
            alt={`G2 ${b.label} award - BuddiesHR`}
            width={73}
            height={76}
            style={{ width: 73, height: 'auto', display: 'block', flexShrink: 0, filter: 'drop-shadow(0 2px 5px rgba(31,27,22,.12))' }}
          />
        ))}
      </div>
    </div>
  );
}

function Testimonials() {
  const testimonials = [
    { quote: "I've had a really good experience with Billy. It has streamlined recognition for my team, and consistently engages everyone through birthday and anniversary celebrations without me needing to manage it on a daily basis.", author: "Jonathan Baldwin", role: "Director of Customer Service & Support, EverConnect", color: 'var(--app-billy)', pic: '/img/love/user.png' },
    { quote: "We love this little app at Travalyst, it makes enhancing our remote-first team culture so easy, and helps bring some joy and levity to their working weeks.", author: "Hannah P.", role: "HR Consultant", color: 'var(--app-alfy)', pic: '/img/love/user.png' },
    { quote: "Clappy was recognised multiple times at our Company All-Hands as a valuable tool that has reinvigorated our recognition culture. We're thrilled with the impact it's had!", author: "Ahlam Said", role: "People Partner", color: 'var(--app-clappy)', pic: '/img/love/a.png' },
    { quote: "My team really enjoy BuddiesHR, multiple functions (Celebrations, Kudos, Surveys...) in one. They're also quick to make updates: I was pleasantly surprised when a suggestion of mine was incorporated into the platform.", author: "Sammie Z.", role: "People Partner", color: 'var(--app-pulsy)', pic: '/img/love/user.png' },
    { quote: "One general message in the channel with the main stats of mood and blockers, and all the replies in the thread, I didn't see such an approach in any other daily bot solution. It's super useful.", author: "Kateryna Morozova", role: "Revenue Operations, Ideals", color: 'var(--app-stany)', pic: '/img/love/kat.jpg' },
    { quote: "I used to do personalised birthday shoutouts myself for the whole team of ~70 people, and it was so time consuming. Billy saves me so much time and our team still feels valued and celebrated.", author: "Chrysiida Koutiva", role: "People Manager, EverAI", color: 'var(--app-billy)', pic: '/img/love/chrysiida.jpeg' },
    { quote: "I installed Alfy to pair our design team since we're remote across regions. It reduces the mental load of scheduling something weekly. Super easy to set up and it's been running seamlessly.", author: "Kevin Santos", role: "Designer, utility NYC", color: 'var(--app-alfy)', pic: '/img/love/kevin.jpg' },
    { quote: "The Clappy app has reinforced our company values and enhanced our culture by recognizing our employees regularly.", author: "Carlye Klick", role: "Executive Director", color: 'var(--app-clappy)', pic: '/img/love/carlye.jpeg' },
    { quote: "What stands out with BuddiesHR is the blend of recognition, networking, and feedback seamlessly integrated into our daily Slack interactions.", author: "Melissa B.", role: "HR Partner", color: 'var(--app-pulsy)', pic: '/img/love/m.png' },
  ];
  const [idx, setIdx] = React.useState(0);
  const trackRef = React.useRef(null);
  const [PER_VIEW, setPerView] = React.useState(3);
  React.useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 640px)');
    const mqTablet = window.matchMedia('(max-width: 980px)');
    const compute = () =>
      setPerView(mqMobile.matches ? 1 : mqTablet.matches ? 2 : 3);
    compute();
    mqMobile.addEventListener('change', compute);
    mqTablet.addEventListener('change', compute);
    return () => {
      mqMobile.removeEventListener('change', compute);
      mqTablet.removeEventListener('change', compute);
    };
  }, []);
  const maxIdx = Math.max(0, testimonials.length - PER_VIEW);
  React.useEffect(() => { setIdx((i) => Math.min(i, maxIdx)); }, [maxIdx]);
  const go = (dir) => setIdx(i => Math.max(0, Math.min(maxIdx, i + dir)));
  return (
    <section className="bh-section" style={{ background: 'var(--bh-cream-deep)', borderTop: '1px solid var(--bh-line)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32, marginBottom: 56, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 auto' }}>
            <div className="bh-chip" style={{ marginBottom: 16 }}>💬 ⭐ 4.8 on G2</div>
            <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
              1,000+ HR teams.<br/>
              <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>Here's what they say.</span>
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button
              onClick={() => go(-1)}
              disabled={idx === 0}
              aria-label="Previous testimonials"
              className="bh-raise"
              style={{
                width: 52, height: 52, borderRadius: '50%',
                background: '#fff', border: '1.5px solid var(--bh-line-strong)',
                cursor: idx === 0 ? 'not-allowed' : 'pointer',
                opacity: idx === 0 ? 0.4 : 1,
                fontSize: 20, color: 'var(--bh-ink)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, lineHeight: 1,
              }}>←</button>
            <button
              onClick={() => go(1)}
              disabled={idx >= maxIdx}
              aria-label="Next testimonials"
              className="bh-raise"
              style={{
                width: 52, height: 52, borderRadius: '50%',
                background: '#fff', border: '1.5px solid var(--bh-line-strong)',
                cursor: idx >= maxIdx ? 'not-allowed' : 'pointer',
                opacity: idx >= maxIdx ? 0.4 : 1,
                fontSize: 20, color: 'var(--bh-ink)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, lineHeight: 1,
              }}><span className="bh-arrow">→</span></button>
          </div>
        </div>
        <div style={{ overflow: 'hidden', margin: '0 -10px', padding: '12px 10px 16px' }}>
          <div
            ref={trackRef}
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: `calc((100% - ${(PER_VIEW - 1) * 20}px) / ${PER_VIEW})`,
              gap: 20,
              transform: `translateX(calc(${-idx} * (((100% - ${(PER_VIEW - 1) * 20}px) / ${PER_VIEW}) + 20px)))`,
              transition: 'transform .5s cubic-bezier(.4,.0,.2,1)',
            }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 'var(--bh-r-lg)',
                padding: 28, border: '1.5px solid var(--bh-line)',
                boxShadow: 'var(--bh-shadow-sm)',
                position: 'relative', overflow: 'hidden',
                display: 'flex', flexDirection: 'column', height: '100%',
              }}>
                <div style={{ position: 'absolute', top: -20, right: -10, fontFamily: 'var(--bh-font-display)', fontSize: 140, color: t.color, opacity: .12, lineHeight: 1, fontStyle: 'italic' }}>"</div>
                <div style={{ display: 'flex', gap: 2, marginBottom: 14, position: 'relative' }}>
                  {Array.from({length: 5}).map((_, j) => <span key={j} style={{ color: 'var(--bh-sun-400)', fontSize: 16 }}>★</span>)}
                </div>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--bh-ink)', position: 'relative', zIndex: 1 }}>{t.quote}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto', paddingTop: 20, borderTop: '1px solid var(--bh-line)' }}>
                  {t.pic ? (
                    <img
                      src={t.pic}
                      alt={`${t.author}, BuddiesHR customer`}
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: t.color, flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                      {t.author.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t.author}</div>
                    <div style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center', gap: 8 }}>
          {Array.from({ length: maxIdx + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              style={{
                width: i === idx ? 24 : 8, height: 8, borderRadius: 999,
                background: i === idx ? 'var(--bh-ink)' : 'var(--bh-line-strong)',
                border: 'none', cursor: 'pointer',
                transition: 'all .3s',
                padding: 0, flexShrink: 0,
              }}
            />
          ))}
        </div>
        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
          <Link href="/reviews" className="bh-btn secondary" style={{ textDecoration: 'none' }}>
            Read more reviews <span className="bh-arrow">→</span>
          </Link>
          <G2RatingStrip centered />
        </div>
      </div>
    </section>
  );
}

// ═══ CTA FOOTER ═══
function FooterCTA() {
  return (
    <section className="bh-section" style={{
      paddingBottom: 0,
      background: 'var(--bh-ink)', color: 'var(--bh-cream)',
      position: 'relative', overflow: 'hidden',
      borderTop: '1px solid rgba(255,248,238,.1)',
    }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'relative', paddingBottom: 24 }}>
        <h2 style={{ fontSize: 'clamp(38px, 5.6vw, 70px)', letterSpacing: '-0.035em', lineHeight: 1.02, color: 'var(--bh-cream)' }}>
          The fastest way to improve<br/>your team's experience<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-300)' }}>is to install one app and see.</span>
        </h2>
        <p style={{ fontSize: 18, color: 'rgba(255,248,238,.7)', marginTop: 20, maxWidth: 700, margin: '20px auto 0', lineHeight: 1.55 }}>
          30-day free trial. Free forever under 10 employees. Public pricing after that. Uninstall in one click if it's not for you. <strong style={{ color: 'var(--bh-cream)' }}>No sales call in sight.</strong>
        </p>
        <div style={{ marginTop: 36, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link href="/get-started" className="bh-btn primary on-dark">
            <img src="/img/redesign/slack-logo.png" alt="" style={{ width: 22, height: 22, marginRight: 8, verticalAlign: '-5px' }} /> Start free in Slack
          </Link>
          <Link href="/get-started" className="bh-btn secondary on-dark">
            <img src="/img/redesign/teams-logo.png" alt="" style={{ width: 22, height: 22, marginRight: 8, verticalAlign: '-5px' }} /> Start free in Teams
          </Link>
        </div>
        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <Link href="/demo" style={{
            color: 'rgba(255,248,238,.65)',
            fontSize: 14.5,
            fontWeight: 500,
            textDecoration: 'underline',
            textDecorationColor: 'rgba(255,248,238,.3)',
            textUnderlineOffset: 4,
            letterSpacing: '.005em',
          }}>
            Or, if you'd rather, book a 15-min demo <span className="bh-arrow">→</span>
          </Link>
        </div>
        </div>

        {/* Build-vs-buy invite, points to the standalone essay */}
        <Link href="/build-vs-buy" style={{
          display: 'block',
          marginTop: 80,
          padding: '26px 32px',
          background: 'rgba(255,248,238,.04)',
          border: '1px solid rgba(255,248,238,.18)',
          borderRadius: 'var(--bh-r-lg)',
          textDecoration: 'none',
          color: 'var(--bh-cream)',
          textAlign: 'left',
          position: 'relative',
          overflow: 'hidden',
          transition: 'background .15s ease, border-color .15s ease',
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,248,238,.07)'; e.currentTarget.style.borderColor = 'rgba(255,159,28,.5)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,248,238,.04)'; e.currentTarget.style.borderColor = 'rgba(255,248,238,.18)'; }}
        >
          <div className="bh-stack" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'center' }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em',
                color: 'var(--bh-sun-300)', marginBottom: 10,
              }}>🛠️ For the engineering-curious</div>
              <div style={{
                fontFamily: 'var(--bh-font-display)', fontSize: 22, fontWeight: 500,
                letterSpacing: '-0.015em', lineHeight: 1.3,
              }}>
                Thinking of building this in-house?{' '}
                <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-300)' }}>Read the full essay first.</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13.5, color: 'rgba(255,248,238,.6)' }}>
                The real numbers from teams who tried both, true cost, a 12-month timeline, and what "just a Slack bot" actually contains.
              </div>
            </div>
            <span style={{
              fontSize: 14, fontWeight: 600,
              color: 'var(--bh-sun-300)',
              whiteSpace: 'nowrap',
            }}>Read it <span className="bh-arrow">→</span></span>
          </div>
        </Link>

        <div style={{ paddingTop: 80 }} />
      </div>
    </section>
  );
}

// ═══ BUILD vs BUY ═══
// Sits after Comparison (vs Lattice/CultureAmp), handles the *other* alternative:
// "we'll just have someone build a Slack bot."
function BuildVsBuy() {
  const questions = [
    {
      n: '01',
      q: 'Is there already a tool focused on solving this?',
      lead: 'Building it once is the easy part. Maintaining it for three years is where these projects quietly die.',
      tag: 'Timezones · Slack rate limits · Teams cards · GDPR deletion · HRIS imports',
      accent: 'var(--app-pulsy)',
      accentBg: 'var(--app-pulsy-bg)',
    },
    {
      n: '02',
      q: 'Do you have someone with the appetite to maintain it?',
      lead: 'Spinning up a script in a sprint isn\'t the same job as owning a product for years.',
      tag: 'Slack API deprecations · Docs for the next hire · The 3am birthday alert in São Paulo',
      accent: 'var(--app-palmy)',
      accentBg: 'var(--app-palmy-bg)',
    },
    {
      n: '03',
      q: 'What happens if that person leaves?',
      lead: 'This is the question that quietly burns HR teams.',
      tag: 'Code in someone\'s old .env · No retention policy · Employee data with no owner',
      accent: 'var(--app-perf)',
      accentBg: 'var(--app-perf-bg)',
    },
  ];

  return (
    <section id="build-vs-buy" style={{
      padding: '88px 40px',
      background: 'var(--bh-cream-deep)',
      borderTop: '1px solid var(--bh-line)',
      borderBottom: '1px solid var(--bh-line)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* ambient blob, same vocabulary as Hero */}
      <div className="bh-blob" style={{
        position: 'absolute', top: -180, left: -200, width: 560, height: 560,
        background: 'radial-gradient(circle at 50% 50%, var(--bh-coral-200), transparent 65%)',
        opacity: .3, filter: 'blur(8px)', zIndex: 0,
      }} />

      <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* header */}
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 40px' }}>
          <div className="bh-chip" style={{ marginBottom: 14 }}>🛠️ Build vs. buy</div>
          <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            Thinking of building this in-house?{' '}
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>Three questions first.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'var(--bh-ink-soft)', marginTop: 14, lineHeight: 1.5 }}>
            Most of our customers considered a "quick Slack bot" before they found us. Here's the conversation we have with them.
          </p>
        </div>

        {/* three question cards, scannable, no nested lists */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {questions.map((qq) => (
            <article key={qq.n} style={{
              background: '#fff',
              border: '1.5px solid var(--bh-line)',
              borderRadius: 'var(--bh-r-lg)',
              boxShadow: 'var(--bh-shadow-sm)',
              padding: '24px 24px 22px',
              display: 'flex', flexDirection: 'column',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: qq.accent,
              }} />

              <span style={{
                fontFamily: 'Boldonse, serif', fontSize: 12,
                color: qq.accent, letterSpacing: '-0.01em',
                lineHeight: 1, marginBottom: 12, alignSelf: 'flex-start',
              }}>{qq.n}</span>

              <h3 style={{
                fontFamily: 'var(--bh-font-display)', fontSize: 20, fontWeight: 600,
                letterSpacing: '-0.02em', lineHeight: 1.2, color: 'var(--bh-ink)',
                margin: '0 0 10px',
              }}>{qq.q}</h3>

              <p style={{
                fontSize: 14.5, color: 'var(--bh-ink-soft)',
                lineHeight: 1.5, margin: '0 0 16px',
              }}>{qq.lead}</p>

              <div style={{
                marginTop: 'auto',
                padding: '12px 14px',
                background: qq.accentBg,
                borderRadius: 'var(--bh-r-sm)',
                fontSize: 12, color: 'var(--bh-ink-soft)',
                lineHeight: 1.55,
                fontFamily: 'var(--bh-font-mono)',
              }}>
                {qq.tag}
              </div>
            </article>
          ))}
        </div>

        {/* slimmer build/buy balance row */}
        <div style={{
          marginTop: 18,
          background: '#fff',
          border: '1.5px solid var(--bh-line)',
          borderRadius: 'var(--bh-r-lg)',
          overflow: 'hidden',
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          boxShadow: 'var(--bh-shadow-sm)',
        }}>
          <div style={{ padding: '26px 28px', borderRight: '1px solid var(--bh-line)', background: 'var(--bh-cream)' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 16,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--bh-sun-500)' }} />
              <h4 style={{
                fontFamily: 'var(--bh-font-display)', fontSize: 26, fontWeight: 600,
                letterSpacing: '-0.02em', lineHeight: 1, color: 'var(--bh-ink)',
                margin: 0,
              }}>Build, <span style={{ fontStyle: 'italic', color: 'var(--bh-sun-600)' }}>if</span></h4>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
              {[
                'It\'s truly unique to your company',
                'You have at least 1 employee committed to ownership',
                'Maintenance is on a job description',
                'It\'s strategic, a product, not a tool',
              ].map((t, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14.5, lineHeight: 1.45, color: 'var(--bh-ink)' }}>
                  <span style={{
                    flexShrink: 0, marginTop: 7,
                    width: 5, height: 5, borderRadius: '50%', background: 'var(--bh-sun-500)',
                  }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div style={{ padding: '26px 28px', background: 'var(--app-clappy-bg)' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 16,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--app-clappy)' }} />
              <h4 style={{
                fontFamily: 'var(--bh-font-display)', fontSize: 26, fontWeight: 600,
                letterSpacing: '-0.02em', lineHeight: 1, color: 'var(--bh-ink)',
                margin: 0,
              }}>Buy, <span style={{ fontStyle: 'italic', color: 'var(--app-clappy)' }}>if</span></h4>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
              {[
                'It\'s a solved problem, kudos, surveys, birthdays, PTO',
                'HR is the buyer, not engineering',
                'You want it live this week, not in Q3',
                'You\'d rather your team focus on what\'s unique to you',
              ].map((t, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14.5, lineHeight: 1.45, color: 'var(--bh-ink)' }}>
                  <span style={{
                    flexShrink: 0, marginTop: 7,
                    width: 5, height: 5, borderRadius: '50%', background: 'var(--app-clappy)',
                  }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* the close, kept, but tighter */}
        <div style={{
          marginTop: 18,
          background: 'var(--bh-ink)', color: 'var(--bh-cream)',
          borderRadius: 'var(--bh-r-lg)',
          padding: '24px 32px',
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 24,
          alignItems: 'center',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at 90% 30%, rgba(255,159,28,.18), transparent 55%)',
            pointerEvents: 'none',
          }} />
          <p style={{
            position: 'relative', zIndex: 1,
            fontFamily: 'var(--bh-font-display)', fontSize: 18, fontWeight: 500,
            letterSpacing: '-0.01em', lineHeight: 1.4,
            color: 'rgba(255,248,238,.88)',
            margin: 0,
          }}>
            Most teams who build it end up patching half-finished bots every month.{' '}
            <span style={{ color: 'var(--bh-sun-300)', fontStyle: 'italic' }}>BuddiesHR is what they were trying to build</span>, without the maintenance bill.
          </p>
          <Link href="/get-started" className="bh-btn primary on-dark" style={{
            position: 'relative', zIndex: 1,
            fontSize: 14, padding: '10px 20px',
            justifyContent: 'center', whiteSpace: 'nowrap',
          }}>Skip the build <span className="bh-arrow">→</span></Link>
        </div>
      </div>
    </section>
  );
}

export {
  Hero,
  HeroProductScreen,
  G2RatingStrip,
  LogoMarquee,
  WhyLeave,
  NativeNotBot,
  WhereWork,
  Pillars,
  Modular,
  TryItDontDemo,
  Comparison,
  BuildVsBuy,
  Testimonials,
  FooterCTA,
};
